import type { FC } from 'react'
import { useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import {
  Box,
  type BoxProps,
  Button,
  chakra,
  Collapse,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Icon,
  Input
} from '@chakra-ui/react'
import { BiChevronDown, BiChevronUp } from 'react-icons/bi'
import { overrideText } from 'ui/lib/overrides'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import { useUIGlobalsContext } from 'ui/lib/ui-globals/hooks'

const ChakraForm = chakra('form')

export interface DiscountCodeFormType {
  code: string
}

interface IDiscountCodeProps extends BoxProps {
  onFormSubmit?: (data: DiscountCodeFormType) => void | Promise<void>
  collapsible?: boolean
  toggleText?: string
}

export interface DiscountCodeFormOverrides {
  discountFormName?: string
  discountFormBtnLabel?: string
  discountFormCodePlaceholder?: string
  discountFormErrorNotFound?: string
}

const DiscountCodeForm: FC<IDiscountCodeProps> = ({
  onFormSubmit,
  collapsible: _collapsible,
  toggleText,
  ...props
}) => {
  const { collapsibleDiscountCodeForm } = useUIGlobalsContext()
  const collapsible = _collapsible ?? collapsibleDiscountCodeForm
  const [isOpen, setIsOpen] = useState(!collapsible)
  const overrides = useOverridesContext<keyof DiscountCodeFormOverrides>()
  const [discountCodeApiError, setDiscountCodeApiError] = useState('')

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid },
    reset,
    setFocus
  } = useForm<DiscountCodeFormType>({
    defaultValues: {
      code: ''
    }
  })

  const onSubmit = async (data: DiscountCodeFormType) => {
    try {
      setDiscountCodeApiError('')

      await onFormSubmit?.(data)
      reset()
    } catch (error) {
      setTimeout(() => {
        setFocus('code')
        setDiscountCodeApiError(error instanceof Error ? error.message : (error as string))
      }, 100)
    }
  }

  const toggleForm = () => setIsOpen(!isOpen)

  const labelText = overrideText('Vill du lägga till en rabattkod?', overrides?.discountFormName)
  const displayToggleText = overrideText(labelText, toggleText)
  const buttonText = overrideText('Lägg till', overrides?.discountFormBtnLabel)
  const requestError =
    discountCodeApiError?.toLowerCase() === 'not found'
      ? overrideText('Ogiltig rabattkod', overrides?.discountFormErrorNotFound)
      : discountCodeApiError

  const formContent = (
    <ChakraForm
      onSubmit={(e) => {
        handleSubmit(onSubmit)(e)
      }}
      {...props}
    >
      <Controller
        name="code"
        control={control}
        rules={{ required: true }}
        render={({ field: { onChange, ref, ...field }, fieldState }) => (
          <FormControl isInvalid={!!fieldState.error?.message || !!requestError} flex="1">
            {!collapsible && <FormLabel fontSize="sm">{labelText}</FormLabel>}

            <HStack gap={2}>
              <Input
                ref={ref}
                placeholder={overrideText('Rabattkod', overrides?.discountFormCodePlaceholder)}
                borderRadius="sm"
                borderColor="primary"
                bg="background.default"
                size="sm"
                isDisabled={isSubmitting}
                onChange={(e) => {
                  onChange(e)
                  setDiscountCodeApiError('')
                }}
                {...field}
              />

              <Box>
                <Button
                  type="submit"
                  size="sm"
                  _hover={{ opacity: 0.9 }}
                  isLoading={isSubmitting}
                  isDisabled={!isValid}
                >
                  {buttonText}
                </Button>
              </Box>
            </HStack>

            <FormErrorMessage>{fieldState.error?.message || requestError}</FormErrorMessage>
          </FormControl>
        )}
      />
    </ChakraForm>
  )

  if (!collapsible) {
    return formContent
  }

  return (
    <Box {...props}>
      <Button
        variant="ghost"
        onClick={toggleForm}
        rightIcon={<Icon as={isOpen ? BiChevronUp : BiChevronDown} />}
        p={0}
        h="auto"
        fontWeight="normal"
        size="sm"
        opacity={0.8}
        _focusVisible={{ boxShadow: 'none' }}
        _hover={{ bg: 'transparent' }}
      >
        {displayToggleText}
      </Button>
      <Collapse in={isOpen} animateOpacity>
        <Box pt={2}>{formContent}</Box>
      </Collapse>
    </Box>
  )
}

export default DiscountCodeForm
